/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsVerify } from "../funcs/identityProviderConnectionsVerify.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  VerifyIdentityProviderConnectionRequest,
  VerifyIdentityProviderConnectionSecurity,
} from "../models/operations/verifyidentityproviderconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type VerifyIdentityProviderConnectionMutationVariables = {
  request: VerifyIdentityProviderConnectionRequest;
  security?: VerifyIdentityProviderConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type VerifyIdentityProviderConnectionMutationData =
  OktaIdentityProviderConnection;

export type VerifyIdentityProviderConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * verify identityProviderConnections
 *
 * @remarks
 * Re-verify the connection against Okta: mint a token, confirm each required scope with a read, and record the outcome. Rate limited per organization. Requires org:admin.
 */
export function useVerifyIdentityProviderConnectionMutation(
  options?: MutationHookOptions<
    VerifyIdentityProviderConnectionMutationData,
    VerifyIdentityProviderConnectionMutationError,
    VerifyIdentityProviderConnectionMutationVariables
  >,
): UseMutationResult<
  VerifyIdentityProviderConnectionMutationData,
  VerifyIdentityProviderConnectionMutationError,
  VerifyIdentityProviderConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildVerifyIdentityProviderConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyVerifyIdentityProviderConnection(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "verify"];
}

export function buildVerifyIdentityProviderConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: VerifyIdentityProviderConnectionMutationVariables,
  ) => Promise<VerifyIdentityProviderConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyVerifyIdentityProviderConnection(),
    mutationFn: function verifyIdentityProviderConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<VerifyIdentityProviderConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsVerify(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
